// Import necessary modules
import { app, auth, Timestamp } from './firebase.js';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, EmailAuthProvider, updatePassword, updateEmail, reauthenticateWithCredential, sendPasswordResetEmail, sendEmailVerification, createUserWithEmailAndPassword } from 'firebase/auth'
import { getPremiumStatus } from './getUpgradedStatus';
import { getCheckoutUrl, getPortalUrl } from './stripePayment';
import { addLoading, removeLoading } from './loading.js';
import { getFirestore, doc, getDoc, getDocs, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDatabase, ref, update, get, serverTimestamp, push, set, remove } from "firebase/database";

// SIGH
import { theAllSeeingEye } from './wordChecker.js'

// Initialize Firestore
const db = getFirestore(app);
const rdb = getDatabase();
let userInfo = {}
let gameInfo = {}
let publicInfo = {}
let userInfoRef;
let gameInfoRef;
let publicInfoRef;

// FUNCTIONS //
const functions = getFunctions();
const deleteAccount = httpsCallable(functions, "deleteUserAccount");
const createLogFunction = httpsCallable(functions, "createLog");
const addBlueberriesToUser = httpsCallable(functions, "addBlueberriesToUser");
const buyPenguin = httpsCallable(functions, "buyPenguin");
const checkUserReward = httpsCallable(functions, "checkUserReward");
const claimDailyReward = httpsCallable(functions, "claimDailyReward");

export async function addPendingSkin(userId, skinData) {
  const userPendingRef = ref(rdb, `community/pendingSkins/${userId}`);
  
  // Generate a new unique key under the user's pendingSkins node
  const newSkinRef = push(userPendingRef);
  
  // Save the skinData at the new unique key
  await set(newSkinRef, skinData);
}

export async function deletePendingSkin(userId, skinId) {
  if (!userId || !skinId) throw new Error("User ID and Skin ID are required");

  const skinRef = ref(rdb, `community/pendingSkins/${userId}/${skinId}`);

  try {
    await remove(skinRef);
  } catch (error) {
    console.error("Failed to delete skin:", error);
    throw error;
  }
}

export function toggleSkinVote(skinId, userId, hasVoted) {
    const votesRef = ref(rdb, `community/approvedSkins/${skinId}/votes`);
    const updateData = hasVoted
        ? { [userId]: true }      // add vote
        : { [userId]: null };     // delete vote
    update(votesRef, updateData)
        .catch((err) => {
        console.error(`Failed to toggle vote for skin "${skinId}":`, err);
        });
}

export async function getApprovedSkins() {
    let approvedSkins = []

  // --- Realtime DB: get all approved skins ---
  try {
    const approvedSkinsRef = ref(rdb, 'community/approvedSkins');
    const snapshot = await get(approvedSkinsRef);
    if (snapshot.exists()) {
      approvedSkins = snapshot.val();
    } else {
      console.warn('No approvedSkins found in Realtime DB');
    }
  } catch (err) {
    console.error("Error loading approved skins:", err);
  }

  return approvedSkins;
}

export async function getAnalytics() {
    let analytics = []

  // --- Realtime DB: get all approved skins ---
  try {
    const analyticsRef = ref(rdb, 'community/analytics');
    const snapshot = await get(analyticsRef);
    if (snapshot.exists()) {
      analytics = snapshot.val();
    } else {
      console.warn('No analytics found in Realtime DB');
    }
  } catch (err) {
    console.error("Error loading approved skins:", err);
  }

  return analytics;
}

export async function getPendingSkins(userId) {
    let pendingSkins = []

  // --- Realtime DB: get all approved skins ---
  try {
    const pendingRef = ref(rdb, `community/pendingSkins/${userId}`);
    const snapshot = await get(pendingRef);
    if (snapshot.exists()) {
      pendingSkins = snapshot.val();
    } else {
      console.warn('No approvedSkins found in Realtime DB');
    }
  } catch (err) {
    console.error("Error loading approved skins:", err);
  }

  return pendingSkins;
}

  // --- Realtime DB: get last 20 chat messages ---
//   try {
//     const chatRef = ref(rdb, 'community/chat');
//     const chatSnapshot = await get(query(chatRef, orderByKey(), limitToLast(20)));
//     if (chatSnapshot.exists()) {
//       const chatData = chatSnapshot.val();
//       results.recentChat = Object.entries(chatData)
//         .map(([timestamp, message]) => ({ timestamp, ...message }))
//         .sort((a, b) => a.timestamp - b.timestamp); // sort oldest to newest
//     }
//   } catch (err) {
//     console.error("Error loading recent chat messages:", err);
//   }

export async function trigger_ClaimDailyReward() {
    if (!auth.currentUser) {
        console.error("User is not authenticated.");
        return false;
    }

    const data = {
        userId: auth.currentUser.uid,
        userDate: Date.now(),
    };

    try {
        const result = await claimDailyReward(data);

        if (result.data.success) {
            await updateLogs('blueberries', 'daily reward', 'penguinClicker');
            return true; // Reward claimed successfully
        } else {
            console.error("Error from claimDailyReward:", result.data.message);
            return false; // Claim failed
        }
    } catch (error) {
        console.error("Error calling claimDailyReward:", error);
        return false; // Error occurred
    }
}

export function timeSinceLastPlay(lastTimeStamp) {
    if (!lastTimeStamp) {
        return "Unknown";
    }

    let lastPlayed;

    if (typeof lastTimeStamp === "object" && "seconds" in lastTimeStamp) {
        // Firebase timestamp (convert seconds to milliseconds)
        lastPlayed = lastTimeStamp.seconds * 1000;
    } else if (typeof lastTimeStamp === "number") {
        // Old Date.now() format (already in milliseconds)
        lastPlayed = lastTimeStamp;
    } else if (typeof lastTimeStamp === "string") {
        // ISO 8601 timestamp string (convert to milliseconds)
        lastPlayed = new Date(lastTimeStamp).getTime();
        if (isNaN(lastPlayed)) return "Unknown"; // Invalid date check
    } else {
        return "Unknown";
    }

    const now = Date.now();
    const diffMs = now - lastPlayed; // Difference in milliseconds

    const minutes = Math.round(diffMs / (1000 * 60));
    const hours = Math.round(diffMs / (1000 * 60 * 60));
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
        return {time: `${minutes} minute${minutes === 1 ? "" : "s"} ago`, recentPlay: true};
    } else if (hours < 24) {
        return {time: `${hours} hour${hours === 1 ? "" : "s"} ago`, recentPlay: false};
    } else {
        return {time: `${days} day${days === 1 ? "" : "s"} ago`, recentPlay: false};
    }
}

export async function checkBirthdayReward(){
    const userId = auth.currentUser?.uid;
    if (!userId) return false;
  
    const SRRef = ref(rdb, `penguinclicker/specialReward`);
    try {
        // Fetch the leaderboard data once
        const snapshot = await get(SRRef);
        const players = snapshot.val();
        // Check if user is already in the list
        if (players[userId]) {
          return false; // User already received reward
        }
        else{
            return true
        }
    }catch(error){
        return false
    }
}

export async function activateBirthdayReward() {
    const userId = auth.currentUser?.uid;
    if (!userId) return false;

    try {
        // Call the Firebase function and properly access `.data`
        const canReward = await checkUserReward({ uid: userId });
        
        if (canReward.data) { // `canReward.data` contains `true` or `false`
            const data = {
                userId: userId,
                amount: 200,
            };

            await addBlueberriesToUser(data);
            const skinID = "55_Birthday_02_04_2025";
            await buyPenguin({ userId, penguin: skinID });

            return true;
        }
    } catch (error) {
        console.log("Something went wrong", error);
    }

    return false;
}

export async function buySkin(penguin) {
    try {
      const userId = auth.currentUser.uid
      const result = await buyPenguin({ userId, penguin });
      console.log("Response from buySkin:", result.data);
      if(result.data == 'Success'){
        await updateLogs('inGamePurchase', penguin, 'penguinClicker')
        return true;
      }else{
        return false;
      }
    } catch (error) {
      console.error("Error calling buySkin:", error.message);
    }
  }

export async function updateResetBlueberries() {
    const data = {
        userId: auth.currentUser.uid,
        amount: 200,
    };
    try {
        const result = await addBlueberriesToUser(data);
        if (result.data.success) {
            await updateLogs('blueberries', 'reset - 200', 'penguinClicker')
            return true;
        } else {
            console.error("Error from addBlueberriesToUser:", result.data.message);
            return false;
        }
    } catch (error) {
        console.error("Error calling addBlueberriesToUser:", error);
        return false;
    }
}


export async function updatePenguinPassBlueberries(amount) {
    const data = {
        userId: auth.currentUser.uid,
        amount: amount,
    };
    try {
        const result = await addBlueberriesToUser(data);
        if (result.data.success) {
            await updateLogs('blueberries', 'penguinPass - 200', 'penguinClicker')
            return true;
        } else {
            console.error("Error from addBlueberriesToUser:", result.data.message);
            return false;
        }
    } catch (error) {
        console.error("Error calling addBlueberriesToUser:", error);
        return false;
    }
}

// Function to update the penguinClicker logs in Firestore
export async function updateLogs(tag, details, accessPoint) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    try {
        const data = {
            userId: auth.currentUser.uid,
            accessPoint: accessPoint,
            tag: tag,
            userName: userInfo.userName || null,
            details: details || null,
            id: userInfo.id || null,
        }

      const response = await createLogFunction(data);
  
      if (response.data.success) {
        console.log("Log created successfully");
      } else {
        console.error("Failed to create log:", response.data.message);
      }
    } catch (error) {
      console.error("Error calling function:", error.message);
    }
  };

// SignOut
export async function signOut() {
    await updateLogs('signOut', 'user signed out', 'penguinClicker')
    auth.signOut();
    try{
        localStorage.removeItem("gameData");
        localStorage.removeItem("upgrades");
    
        localStorage.removeItem("backupGameData");
        localStorage.removeItem("backupUpgrades");
        localStorage.removeItem("backupSet");
    }
    catch{
        console.log('error Removing Local Storage')
    }
    window.location = "game.html";
}

export async function deleteUser(userFeedback){
    try {
        // Call the Cloud Function and pass the feedback
        await updateLogs('delete', 'user deleted account', 'penguinClicker')
        await deleteAccount({ feedback: userFeedback });
        localStorage.clear();
        return true
        // Optionally, redirect the user to a different page after deletion
      } catch (error) {
        console.error("Error deleting account:", error);
        return falst
    }
}

// SignIn
export const signIn = async () => {
    let signedIn = false;
    try {
        const result = await signInWithPopup(auth, new GoogleAuthProvider());
        const user = result.user;
        if (user) {
            signedIn = true;
            await updateLogs('signIn','user signed in', 'penguinClicker')
        }
        window.location = "game.html"
    } catch (error) {
        console.error('Error signing in:', error);
    }
    return signedIn
}

// Shop Link
export async function checkout(priceId, mode, blueberries, type) {
    try{
        addLoading()
        const checkoutUrl = await getCheckoutUrl(app, priceId, mode, blueberries, type);
        window.location.href = checkoutUrl;
    }catch (error){
        console.log(error)
        removeLoading()
    }

}

// Portal Link
export async function portal() {
    try{
        addLoading()
        const portalUrl = await getPortalUrl(app)
        window.location.href = portalUrl;
    }catch (error){
        console.log(error)
        removeLoading()
    }

}

// Checking if Subscribed
export async function checkPremium() {
    const PremiumStatus = auth.currentUser ? await getPremiumStatus(app) : false;
    return PremiumStatus
}

// Setting New Username
export async function setNewUserName(newUserName) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
    }
    if(newUserName.length <= 20){
        if(!theAllSeeingEye(newUserName.toLowerCase())){
            const userId = auth.currentUser.uid;
            userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');
        
            // Update the 'userName' field in the 'userInfo' document
            try {
                await updateDoc(userInfoRef, {
                    userName: newUserName
                });

                const dataToUpdate = {
                    "userName": newUserName,
                }
                await updateLeaderboardUserData(dataToUpdate)

                return 'success'
            } catch (error) {
                return error
            }
        }else{
            return 'offensive'
        }
    }else{
        return 'long'
    }
}

// Setting New Username
export async function setLetters(letters) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
    }

    const userId = auth.currentUser.uid;
    userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');

    // Update the 'userName' field in the 'userInfo' document
    try {
        await updateDoc(userInfoRef, {
            letters: letters
        });
        return 'success'
    } catch (error) {
        return error
    }
}

export async function getGeneralInfo(){
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    const generalInfoRef = doc(db, 'general', 'info');

    try {
        const generalInfoDoc = await getDoc(generalInfoRef);
        return generalInfoDoc.data();
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

// Setting Terms
export async function setTermsConditions(termsPolicyVersion) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
    }
    const userId = auth.currentUser.uid;
    userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');

    try {
        await updateDoc(userInfoRef, {
            termsPolicyVersion: termsPolicyVersion
        });
        return 'success'
    } catch (error) {
        return error
    }
}

// Setting Friends ** KEEP THIS ONE **
export async function setFriends(friendsObject) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
    }
    const userId = auth.currentUser.uid;
    userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');

    try {
        await updateDoc(userInfoRef, {
            friends: friendsObject
        });
        await updateLeaderboardUserData({friends: friendsObject.requested})
        return 'success'
    } catch (error) {
        return error
    }
}

// Function to fetch current user data from Firestore
export async function getPublicUser() {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    const userId = auth.currentUser.uid;
    userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');

    try {
        const publicDoc = await getDoc(userInfoRef);
        if (publicDoc.exists()) {
            publicInfo = publicDoc.data();
            return publicInfo
        } else {
            console.error('No such document!');
            return null;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

// Function to fetch current user data from Firestore
export async function getUserInfo(game) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    const userId = auth.currentUser.uid;
    userInfoRef = doc(db, 'users', userId, 'general', 'userInfo');
    gameInfoRef = doc(db, 'users', userId, game, 'gameInfo');
    publicInfoRef = doc(db, 'users', userId, 'general', 'public');

    try {
        const InfoDoc = await getDoc(userInfoRef);
        const gameDoc = await getDoc(gameInfoRef);
        const publicDoc = await getDoc(publicInfoRef);
        if (InfoDoc.exists() && gameDoc.exists() && publicDoc.exists()) {
            userInfo = InfoDoc.data();
            gameInfo = gameDoc.data();
            publicInfo = publicDoc.data();
            return [userInfo, gameInfo, publicInfo]
        } else {
            console.error('No such document!');
            return null;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

export async function getOtherSkins(){
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return false;
    }

    const userId = auth.currentUser.uid;
    const privateGameRef = doc(db, 'users', userId, 'penguinclicker', 'private');

    try {
        const privateGameDoc = await getDoc(privateGameRef);
        if (privateGameDoc.exists()) {
            const privateGame = privateGameDoc.data();
            return privateGame
        } else {
            console.error('No such document!');
            return false;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return false;
    }
}

export async function getPrivateData(){
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return false;
    }

    const userId = auth.currentUser.uid;
    const privateUserRef = doc(db, 'users', userId, 'general', 'private');

    try {
        const privateUserDoc = await getDoc(privateUserRef);
        if (privateUserDoc.exists()) {
            const privateUser = privateUserDoc.data();
            return privateUser
        } else {
            console.error('No such document!');
            return false;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return false;
    }
}


// Function to get leaderboard
export async function getLeaderBoard(game, leaderBoard) {
    let returnLeaderBoardData = [];

    let leaderBoardRef = doc(db, game, 'leaderBoards')

    const leaderBoardDoc = await getDoc(leaderBoardRef);

    if(leaderBoardDoc.exists()){
        const leaderBoardData = leaderBoardDoc.data()[leaderBoard]

        for (let i = 0; i < leaderBoardData.length; i++){
            let leaderinfoRef = doc(db, 'users', leaderBoardData[i], 'general', 'userInfo');
            let leaderGameRef = doc(db, 'users', leaderBoardData[i], game, 'gameInfo');
            let leaderObject = {}
            const leaderInfoDoc = await getDoc(leaderinfoRef);
            const leaderGameDoc = await getDoc(leaderGameRef);
    
            // Getting Friend General Game
            if (leaderGameDoc.exists() && leaderInfoDoc.exists()) {
                leaderObject["game"] = leaderGameDoc.data();
                leaderObject["info"] = leaderInfoDoc.data();
                leaderObject["id"] = leaderBoardData[i];
                returnLeaderBoardData.push(leaderObject)
            } else {
                console.error('No such document!');
            }
        }
    }

    returnLeaderBoardData.sort((a, b) => {
        // First, compare by resets in descending order
        if (b.game.resets !== a.game.resets) {
          return b.game.resets - a.game.resets;
        }
        // If resets are equal, compare by PPS in descending order
        return b.game.PPS - a.game.PPS;
    });

    return returnLeaderBoardData
}

// Function to update relevant user data
export async function updateLeaderboardUserData(dataToUpdate) {
    // Reference to the user's data in the leaderboard
    const userId = auth.currentUser.uid;
    const userRef = ref(rdb, `penguinclicker/leaderBoard/${userId}`);

    // Perform the update with the specific fields to change
    try {
        dataToUpdate['timeStamp'] = serverTimestamp();
        await update(userRef, dataToUpdate);
        console.log('Updating LeaderBoard UserData')

        console.log("User data updated successfully");
        return true
    } catch (error) {
        console.error("Error updating user data:", error);

        // Check if the error indicates that the user does not exist
        if (error.code === 'permission-denied' || error.code === 'not-found') {
            // If the user does not exist, create the user in the leaderboard
            await checkLeaderBoardPosition();
            return false
        }
    }
}

export async function getShopData(signedIn) {
    if(signedIn){
        const userId = auth.currentUser.uid;
        const globalShopRef = ref(rdb, `penguinclicker/globalShop`);
        const personalShopRef = ref(rdb, `penguinclicker/personalShops/${userId}`);
    
        const returnShopArray = []
        try {
            // Fetch the leaderboard data once
            const globalShop = await get(globalShopRef);
            const personalShop = await get(personalShopRef);
            if (globalShop.exists()) {
                returnShopArray.push(globalShop.val())
            }
    
            if(personalShop.exists()){
                returnShopArray.push(personalShop.val())
            }
    
            if(returnShopArray.length > 0){
                return returnShopArray; // Return the sorted leaderboard data
            }
            
            else {
                console.log("No shop data available.");
                return null; // Or return an empty object if preferred
            }
        } catch (error) {
            console.log("The read failed: " + error.message);
            return null; // Handle the error as needed
        }
    }else{
        const globalShopRef = ref(rdb, `penguinclicker/globalShop`);
    
        const returnShopArray = []
        try {
            // Fetch the leaderboard data once
            const globalShop = await get(globalShopRef);
            if (globalShop.exists()) {
                returnShopArray.push(globalShop.val())
            }
    
            if(returnShopArray.length > 0){
                return returnShopArray; // Return the sorted leaderboard data
            }
            
            else {
                console.log("No shop data available.");
                return null; // Or return an empty object if preferred
            }
        } catch (error) {
            console.log("The read failed: " + error.message);
            return null; // Handle the error as needed
        }
    }

}

export async function getAllUsersLeaderBoard() {
    const leaderBoardRef = ref(rdb, `penguinclicker/leaderBoard`);

    try {
        // Fetch the leaderboard data once
        const snapshot = await get(leaderBoardRef);
        if (snapshot.exists()) {
            const leaderBoard = snapshot.val();

            // Convert the leaderboard object to an array and sort it by PPS
            const sortedLeaderBoard = Object.entries(leaderBoard)
                .map(([userId, userData]) => ({ userId, ...userData })) // Create an array of user objects
                .sort((a, b) => b.PPS - a.PPS); // Sort by PPS in descending order

            console.log('GET LeaderBoard UserData');

            return sortedLeaderBoard; // Return the sorted leaderboard data
        } else {
            console.log("No leaderboard data available.");
            return null; // Or return an empty object if preferred
        }
    } catch (error) {
        console.log("The read failed: " + error.message);
        return null; // Handle the error as needed
    }
}

// Function to update PPS value in Firestore
export async function updateGameInfo(newGameInfo) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    try {
        if(newGameInfo.PPS != userInfo.PPS ||
            newGameInfo.equippedSkin != userInfo.equippedSkin ||
            newGameInfo.highestUnlocked != userInfo.highestUnlocked
        ){
            if(gameInfoRef){
                // Update the PPS value in the document
                await updateDoc(gameInfoRef, newGameInfo);
            } 
        }
    }
    
    catch (error) {
        console.error('Error updating document:', error);
        return null;
    }
}

// Function to update specific fields in Firestore
export async function updateSingleField(key, gameData) {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    try {
        if (gameInfoRef) {
            // Construct the field update object dynamically
            const updateData = { [key]: gameData };
            await updateDoc(gameInfoRef, updateData);
        }
    } catch (error) {
        console.error('Error updating specific field:', error);
        return null;
    }
}

// Updates the Users General Login Time
export async function updateUserLoginTime() {
    if (!auth.currentUser) {
        console.error('No user signed in!');
        return null;
    }

    try {
        if(userInfoRef){
            // Update the PPS value in the document
            await updateDoc(userInfoRef, {
                lastLogin: Timestamp.now()
            });
        } 
    }
    
    catch (error) {
        console.error('Error updating document:', error);
        return null;
    }
}

// PENGUIN CLICKER //

export async function getPenguinSkins(){
    const skinsRef = doc(db, 'penguinclicker', 'skins');
    let skinsData;
    try {
        const docSnap = await getDoc(skinsRef);
        if (docSnap.exists()) {
            skinsData = docSnap.data();
            return skinsData
        } else {
            console.error('No such document!');
            return null;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

export async function getPenguinPass(){
    const skinsRef = doc(db, 'penguinclicker', 'penguinPasses');
    let penguinPasses;
    try {
        const docSnap = await getDoc(skinsRef);
        if (docSnap.exists()) {
            penguinPasses = docSnap.data();
            return penguinPasses;
        } else {
            console.error('No such document!');
            return null;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

export async function getLists(){
    const listsRef = doc(db, 'penguinclicker', 'lists');
    let listData;
    try {
        const docSnap = await getDoc(listsRef);
        if (docSnap.exists()) {
            listData = docSnap.data();
            return listData
        } else {
            console.error('No such document!');
            return null;
        }
    } 
    
    catch (error) {
        console.error('Error getting document:', error);
        return null;
    }
}

// THIS IS THE OTHER TYPE OF LOGIN

// Create Account with Email Verification
export async function create_EmailPassword(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      window.location = "game.html";
    } catch (error) {
        return error
    }
}

export async function sendVerifyEmail(user){
    await sendEmailVerification(user);
}

// Login
export async function signIn_EmailPassword(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      if (user) {
        await updateLogs('signIn','user signed in', 'penguinClicker')
      }
      window.location = "game.html";
    } catch (error) {
        return error
    }

}

// Forgot Password
export async function forgotPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      return error
    }
}

// Change Email or Password (requires re-authentication)
export async function changeEmailOrPassword(currentPassword, newEmail = null, newPassword = null) {
    const user = auth.currentUser;
    if (user) {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      try {
        await reauthenticateWithCredential(user, credential);
        if (newEmail) await updateEmail(user, newEmail);
        if (newPassword) await updatePassword(user, newPassword);
        return true;
      } catch (error) {
        return error;
      }
    }
}